import type {
  Page,
  PlatformActivity,
  PlatformDashboard,
  PlatformFeedback,
  PlatformFeedbackCounts,
  PlatformFeedbackUpdate,
  PlatformImpersonation,
  PlatformMessageUsage,
  PlatformProfileUpdate,
  PlatformResidencyMoveInput,
  PlatformResidencyZones,
  PlatformSessionInfo,
  PlatformSubscription,
  PlatformSubscriptionCreate,
  PlatformSubscriptionUpdate,
  PlatformTenant,
  PlatformTenantCreate,
  PlatformTenantListItem,
  PlatformTenantModules,
  PlatformTenantResidency,
  PlatformTenantUpdate,
  PlatformTenantUser,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { FeedbackListParams, SubscriptionListParams, TenantListParams } from './search'
import { platformKeys } from './session'

export const tenantKeys = {
  all: [...platformKeys.all, 'tenants'] as const,
  lists: () => [...tenantKeys.all, 'list'] as const,
  list: (query: TenantListParams) => [...tenantKeys.lists(), query] as const,
  detail: (id: string) => [...tenantKeys.all, 'detail', id] as const,
  users: (id: string) => [...tenantKeys.all, 'users', id] as const,
  activity: (id: string) => [...tenantKeys.all, 'activity', id] as const,
}

export function tenantListQueryOptions(query: TenantListParams) {
  return queryOptions({
    queryKey: tenantKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<PlatformTenantListItem>>('/platform/tenants', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function tenantQueryOptions(id: string) {
  return queryOptions({
    queryKey: tenantKeys.detail(id),
    queryFn: ({ signal }) => api.get<PlatformTenant>(`/platform/tenants/${id}`, { signal }),
  })
}

export function tenantUsersQueryOptions(id: string) {
  return queryOptions({
    queryKey: tenantKeys.users(id),
    queryFn: ({ signal }) => api.get<PlatformTenantUser[]>(`/platform/tenants/${id}/users`, { signal }),
  })
}

export function tenantActivityQueryOptions(id: string) {
  return queryOptions({
    queryKey: tenantKeys.activity(id),
    queryFn: ({ signal }) => api.get<PlatformActivity[]>(`/platform/tenants/${id}/activity`, { signal }),
  })
}

/** A changed tenant: its page shows the answer, the lists and its activity load again. */
function useTenantSaved() {
  const queryClient = useQueryClient()
  return (tenant: PlatformTenant) => {
    queryClient.setQueryData(tenantKeys.detail(tenant.id), tenant)
    void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: tenantKeys.activity(tenant.id) })
  }
}

export function useCreateTenant() {
  const saved = useTenantSaved()
  return useMutation({
    mutationFn: (body: PlatformTenantCreate) => api.post<PlatformTenant>('/platform/tenants', body),
    onSuccess: saved,
  })
}

export function useUpdateTenant(id: string) {
  const saved = useTenantSaved()
  return useMutation({
    mutationFn: (body: PlatformTenantUpdate) => api.patch<PlatformTenant>(`/platform/tenants/${id}`, body),
    onSuccess: saved,
  })
}

export function useSetTenantStatus(id: string) {
  const saved = useTenantSaved()
  return useMutation({
    mutationFn: ({ action, reason }: { action: 'suspend' | 'activate'; reason: string }) =>
      api.post<PlatformTenant>(`/platform/tenants/${id}/${action}`, { reason }),
    onSuccess: saved,
  })
}

export function useSetTenantModules(id: string) {
  const saved = useTenantSaved()
  return useMutation({
    mutationFn: (body: PlatformTenantModules) =>
      api.put<PlatformTenant>(`/platform/tenants/${id}/modules`, body),
    onSuccess: saved,
  })
}

export function useResetTenantUserPassword(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      api.post<void>(`/platform/tenants/${tenantId}/users/${userId}/reset-password`, { password }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) })
      void queryClient.invalidateQueries({ queryKey: tenantKeys.activity(tenantId) })
    },
  })
}

export function useUpdatePlatformProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformProfileUpdate) => api.patch<PlatformSessionInfo>('/platform/profile', body),
    onSuccess: (info) => queryClient.setQueryData(platformKeys.session, info),
  })
}

export function useChangePlatformPassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<void>('/platform/auth/change-password', body),
  })
}

export function useImpersonateUser(tenantId: string) {
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      api.post<PlatformImpersonation>(`/platform/tenants/${tenantId}/users/${userId}/impersonate`, {
        reason,
      }),
  })
}

// ── dashboard ────────────────────────────────────────────────────────────────

export const dashboardQueryOptions = queryOptions({
  queryKey: [...platformKeys.all, 'dashboard'] as const,
  queryFn: ({ signal }) => api.get<PlatformDashboard>('/platform/dashboard', { signal }),
})

// ── subscriptions ────────────────────────────────────────────────────────────

export const subscriptionKeys = {
  all: [...platformKeys.all, 'subscriptions'] as const,
  list: (query: SubscriptionListParams) => [...subscriptionKeys.all, 'list', query] as const,
}

export function subscriptionListQueryOptions(query: SubscriptionListParams) {
  return queryOptions({
    queryKey: subscriptionKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<PlatformSubscription>>('/platform/subscriptions', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
          tenantId: query.tenantId,
          endingWithin: query.endingWithin,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Subscriptions changed: every list, the dashboard and the activity load again. */
function useSubscriptionSaved() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: subscriptionKeys.all })
    void queryClient.invalidateQueries({ queryKey: [...platformKeys.all, 'dashboard'] })
    void queryClient.invalidateQueries({ queryKey: [...tenantKeys.all, 'activity'] })
  }
}

export function useCreateSubscription() {
  const saved = useSubscriptionSaved()
  return useMutation({
    mutationFn: (body: PlatformSubscriptionCreate) =>
      api.post<PlatformSubscription>('/platform/subscriptions', body),
    onSuccess: saved,
  })
}

export function useUpdateSubscription() {
  const saved = useSubscriptionSaved()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlatformSubscriptionUpdate }) =>
      api.patch<PlatformSubscription>(`/platform/subscriptions/${id}`, body),
    onSuccess: saved,
  })
}

export function useCancelSubscription() {
  const saved = useSubscriptionSaved()
  return useMutation({
    mutationFn: (id: string) => api.post<PlatformSubscription>(`/platform/subscriptions/${id}/cancel`),
    onSuccess: saved,
  })
}

// ── feedback ─────────────────────────────────────────────────────────────────

export type FeedbackPage = Page<PlatformFeedback> & { counts: PlatformFeedbackCounts }

export const feedbackKeys = {
  all: [...platformKeys.all, 'feedback'] as const,
  list: (query: FeedbackListParams) => [...feedbackKeys.all, 'list', query] as const,
}

export function feedbackListQueryOptions(query: FeedbackListParams) {
  return queryOptions({
    queryKey: feedbackKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<FeedbackPage>('/platform/feedback', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          q: query.q,
          status: query.status,
          type: query.type,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlatformFeedbackUpdate }) =>
      api.patch<PlatformFeedback>(`/platform/feedback/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: [...platformKeys.all, 'dashboard'] })
    },
  })
}

// ── data residency (R0b W10) ─────────────────────────────────────────────────

export const residencyZonesQueryOptions = queryOptions({
  queryKey: [...platformKeys.all, 'residency-zones'] as const,
  queryFn: ({ signal }) => api.get<PlatformResidencyZones>('/platform/residency-zones', { signal }),
  staleTime: 5 * 60_000,
})

/** How often an active move's progress is read again (no refresh needed). */
export const RESIDENCY_POLL_MS = 3000

export function tenantResidencyQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...tenantKeys.all, 'residency', id] as const,
    queryFn: ({ signal }) =>
      api.get<PlatformTenantResidency>(`/platform/tenants/${id}/residency`, { signal }),
    refetchInterval: (query) => (query.state.data?.move?.status === 'ACTIVE' ? RESIDENCY_POLL_MS : false),
  })
}

export function useMoveResidency(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformResidencyMoveInput) =>
      api.post<PlatformTenantResidency>(`/platform/tenants/${id}/residency-move`, body),
    onSuccess: (residency) => {
      client.setQueryData(tenantResidencyQueryOptions(id).queryKey, residency)
      void client.invalidateQueries({ queryKey: tenantKeys.detail(id) })
      void client.invalidateQueries({ queryKey: tenantKeys.activity(id) })
    },
  })
}

// ── message metering (R0b MS10) ──────────────────────────────────────────────

export function tenantMessageUsageQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...tenantKeys.all, 'message-usage', id] as const,
    queryFn: ({ signal }) =>
      api.get<PlatformMessageUsage>(`/platform/tenants/${id}/message-usage`, { signal }),
  })
}
